import React, { useState, useEffect } from "react";
import { Input, List } from "antd";
import { LockOutlined, CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import styled from "styled-components";
import { validatePassword, PasswordValidationResult } from "@/shared/utils/passwordValidation";

const PasswordInputContainer = styled.div`
	position: relative;
`;

const ValidationList = styled(List)`
	position: absolute;
	top: 100%;
	left: 0;
	right: 0;
	background: white;
	border: 1px solid #d9d9d9;
	border-radius: 8px;
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
	z-index: 1000;
	margin-top: 4px;
	max-height: 200px;
	overflow-y: auto;

	.ant-list-item {
		padding: 8px 12px;
		border-bottom: 1px solid #f0f0f0;

		&:last-child {
			border-bottom: none;
		}
	}
`;

const ValidationItem = styled.div<{ isValid: boolean }>`
	display: flex;
	align-items: center;
	gap: 8px;
	font-size: 12px;
	color: ${(props) => (props.isValid ? "#52c41a" : "#ff4d4f")};
`;

const StrengthIndicator = styled.div<{ strength: "weak" | "medium" | "strong" }>`
	margin-top: 8px;
	padding: 4px 8px;
	border-radius: 4px;
	font-size: 11px;
	font-weight: 500;
	text-align: center;
	background: ${(props) => {
		switch (props.strength) {
			case "weak":
				return "#fff2f0";
			case "medium":
				return "#fff7e6";
			case "strong":
				return "#f6ffed";
			default:
				return "#f5f5f5";
		}
	}};
	color: ${(props) => {
		switch (props.strength) {
			case "weak":
				return "#ff4d4f";
			case "medium":
				return "#fa8c16";
			case "strong":
				return "#52c41a";
			default:
				return "#666";
		}
	}};
`;

interface PasswordInputWithValidationProps {
	className?: string;
	id?: string;
	value?: string;
	onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
	onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
	status?: "error" | "warning";
	"aria-describedby"?: string;
	placeholder?: string;
	size?: "small" | "middle" | "large";
	prefix?: React.ReactNode;
	showValidation?: boolean;
	showStrength?: boolean;
	style?: React.CSSProperties;
}

const PasswordInputWithValidation: React.FC<PasswordInputWithValidationProps> = ({
	id,
	value = "",
	onChange,
	onBlur,
	status,
	"aria-describedby": ariaDescribedBy,
	placeholder = "Enter Password",
	size = "large",
	prefix = <LockOutlined />,
	showValidation = true,
	showStrength = false,
	style,
	className,
}) => {
	const [validation, setValidation] = useState<PasswordValidationResult>({
		isValid: false,
		errors: [],
		strength: "weak",
	});
	const [showValidationList, setShowValidationList] = useState(false);

	useEffect(() => {
		if (value) {
			const result = validatePassword(value);
			setValidation(result);
		} else {
			setValidation({
				isValid: false,
				errors: [],
				strength: "weak",
			});
		}
	}, [value]);

	const handleFocus = () => {
		if (showValidation && value) {
			setShowValidationList(true);
		}
	};

	const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
		onBlur?.(e);
		setTimeout(() => setShowValidationList(false), 200);
	};

	const validationItems = [
		{ text: "At least 8 characters", isValid: value.length >= 8 },
		{ text: "At least 1 uppercase letter", isValid: /[A-Z]/.test(value) },
		{ text: "At least 1 lowercase letter", isValid: /[a-z]/.test(value) },
		{ text: "At least 1 number", isValid: /\d/.test(value) },
	];

	return (
		<PasswordInputContainer className={className}>
			<Input.Password
				id={id}
				value={value}
				onChange={onChange}
				placeholder={placeholder}
				size={size}
				prefix={prefix}
				style={style}
				status={status}
				aria-describedby={ariaDescribedBy}
				onFocus={handleFocus}
				onBlur={handleBlur}
				suffix={validation.isValid ? <CheckCircleOutlined style={{ color: "#52c41a" }} /> : null}
			/>

			{showValidation && showValidationList && value && (
				<ValidationList>
					{validationItems.map((item, index) => (
						<List.Item key={index}>
							<ValidationItem isValid={item.isValid}>
								{item.isValid ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
								{item.text}
							</ValidationItem>
						</List.Item>
					))}
					{showStrength && (
						<List.Item>
							<StrengthIndicator strength={validation.strength}>
								Password strength: {validation.strength.toUpperCase()}
							</StrengthIndicator>
						</List.Item>
					)}
				</ValidationList>
			)}
		</PasswordInputContainer>
	);
};

export default PasswordInputWithValidation;
